/**
 * React hooks for WebSocket subscriptions.
 * 
 * This module provides hooks for subscribing to WebSocket channels.
 * 
 * Architecture:
 * - Public data hooks (orderbook, price, vlp) return data directly
 * - User data is now managed by useAccountSync which populates the store
 * - Legacy user hooks are kept for backwards compatibility but simplified
 */

import { useEffect, useState, useRef } from "react";
import {
  wsClient,
  type WsMessage,
  type WsOrderbookMessage,
  type WsPriceMessage,
  type WsVlpQuoteMessage,
  type WsPositionMessage,
  type WsOrderMessage,
  type WsBalanceMessage,
  type ConnectionState,
} from "../ws-client";

/**
 * Generic hook for subscribing to a WebSocket channel.
 * @param channel Channel name (e.g., "orderbook/BTC-USD")
 * @param enabled Whether the subscription is enabled
 * @returns Latest message from the channel
 */
export function useWsSubscription<T extends WsMessage = WsMessage>(
  channel: string | null,
  enabled = true
): { data: T | null; isConnected: boolean } {
  const [data, setData] = useState<T | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!channel || !enabled) {
      setData(null);
      setIsConnected(false);
      return;
    }

    const unsubscribe = wsClient.subscribe<T>(channel, (message) => {
      setData(message);
      setIsConnected(true);
    });

    // Check connection state immediately and periodically
    const checkConnection = () => {
      const state = wsClient.getState(channel);
      setIsConnected(state === "connected");
    };
    
    checkConnection();
    const interval = setInterval(checkConnection, 500);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [channel, enabled]);

  return { data, isConnected };
}

/**
 * Hook for subscribing to orderbook updates.
 * Public data - returns data directly (not managed by store).
 * Filters by message type to ignore control messages.
 */
export function useWsOrderbook(symbol: string | undefined) {
  const channel = symbol ? `orderbook/${symbol}` : null;
  const { data: rawData, isConnected } = useWsSubscription<WsOrderbookMessage>(channel, !!symbol);

  // Only use data if it's actually an orderbook message
  const data = rawData?.type === 'orderbook' ? rawData : null;

  const orderbook = data
    ? {
        bids: data.bids.map((level) => ({
          price: parseFloat(level.price),
          size: parseFloat(level.size),
        })),
        asks: data.asks.map((level) => ({
          price: parseFloat(level.price),
          size: parseFloat(level.size),
        })),
      }
    : { bids: [], asks: [] };

  const bestBid = orderbook.bids[0]?.price || 0;
  const bestAsk = orderbook.asks[0]?.price || 0;
  const midPrice = bestBid && bestAsk ? (bestBid + bestAsk) / 2 : 0;
  const spread = bestAsk - bestBid;
  const spreadPercent = midPrice > 0 ? (spread / midPrice) * 100 : 0;

  return {
    orderbook,
    bestBid,
    bestAsk,
    midPrice,
    spread,
    spreadPercent,
    isConnected,
  };
}

/**
 * Hook for subscribing to price updates.
 * Public data - returns data directly (not managed by store).
 * Filters by message type to ignore control messages.
 */
export function useWsPrice(symbol: string | undefined) {
  const channel = symbol ? `price/${symbol}` : null;
  const { data: rawData, isConnected } = useWsSubscription<WsPriceMessage>(channel, !!symbol);

  // Only use data if it's actually a price message
  const data = rawData?.type === 'price' ? rawData : null;

  return {
    price: data ? parseFloat(data.price) : null,
    sources: data?.sources || [],
    timestamp: data?.timestamp || null,
    isConnected,
  };
}

/**
 * Hook for subscribing to VLP quote updates.
 * Public data - returns data directly (not managed by store).
 * 
 * IMPORTANT: Filters messages by type to ignore control messages (subscribed, pong, etc.)
 * that don't contain VLP quote data.
 */
export function useWsVlpQuote(symbol: string | undefined) {
  const channel = symbol ? `vlp/${symbol}` : null;
  const { data: rawData, isConnected } = useWsSubscription<WsVlpQuoteMessage>(channel, !!symbol);

  // Only use data if it's actually a VLP quote message
  // The WebSocket can receive other message types (subscribed, pong, error)
  // which don't have VLP fields and would cause NaN/undefined issues
  const data = rawData?.type === 'vlp_quote' ? rawData : null;

  return {
    quote: data
      ? {
          market: data.market,
          bidPrice: parseFloat(data.bid_price),
          askPrice: parseFloat(data.ask_price),
          spread: parseFloat(data.spread),
          oraclePrice: parseFloat(data.oracle_price),
          vlpPosition: parseFloat(data.vlp_position),
          timestamp: new Date(data.timestamp),
        }
      : null,
    isConnected,
  };
}

/** Frontend position type from WebSocket (kept for backwards compatibility) */
export interface WsFrontendPosition {
  id: string;
  market: string;
  side: "long" | "short";
  size: number;
  entryPrice: number;
  currentPrice: number;
  leverage: number;
  liquidationPrice: number;
  unrealizedPnl: number;
  realizedPnl: number;
  margin: number;
  status: "open";
  openedAt: Date;
  takeProfit?: number;
  stopLoss?: number;
}

/**
 * Hook for subscribing to user positions.
 * 
 * NOTE: User data is now managed by useAccountSync and stored in the Zustand store.
 * This hook is kept for backwards compatibility but returns data from the store.
 * Use useBackendPositions() instead for the recommended approach.
 * 
 * @deprecated Use useBackendPositions() from use-backend-positions.ts instead
 */
export function useWsPositions(userId: string | undefined) {
  const hasToken = !!wsClient.getToken();
  const canSubscribe = !!userId && hasToken;
  
  const { isConnected } = useWsSubscription<WsPositionMessage>(
    canSubscribe ? "user/positions" : null,
    canSubscribe
  );

  // Return empty - data now comes from store via useBackendPositions
  return {
    positions: [] as WsFrontendPosition[],
    isConnected, // Raw socket connection state
    isSocketConnected: isConnected,
  };
}

/**
 * Hook for subscribing to user orders.
 * 
 * NOTE: User data is now managed by useAccountSync and stored in the Zustand store.
 * This hook is kept for backwards compatibility but returns data from the store.
 * Use useBackendOrders() instead for the recommended approach.
 * 
 * @deprecated Use useBackendOrders() from use-backend-orders.ts instead
 */
export function useWsOrders(userId: string | undefined) {
  const hasToken = !!wsClient.getToken();
  const canSubscribe = !!userId && hasToken;
  
  const { isConnected } = useWsSubscription<WsOrderMessage>(
    canSubscribe ? "user/orders" : null,
    canSubscribe
  );

  // Return empty - data now comes from store via useBackendOrders
  return {
    orders: [] as Array<{
      id: string;
      market: string;
      side: "buy" | "sell";
      orderType: "market" | "limit";
      price: number;
      size: number;
      filledSize: number;
      status: string;
    }>,
    isConnected, // Raw socket connection state
    isSocketConnected: isConnected,
  };
}

/**
 * Hook for subscribing to user balance.
 * 
 * NOTE: User data is now managed by useAccountSync and stored in the Zustand store.
 * This hook is kept for backwards compatibility but returns data from the store.
 * Use useBackendAccount() instead for the recommended approach.
 * 
 * @deprecated Use useBackendAccount() from use-backend-account.ts instead
 */
export function useWsBalance(userId: string | undefined) {
  const hasToken = !!wsClient.getToken();
  const canSubscribe = !!userId && hasToken;
  
  const { isConnected } = useWsSubscription<WsBalanceMessage>(
    canSubscribe ? "user/balances" : null,
    canSubscribe
  );

  // Return null - data now comes from store via useBackendAccount
  return {
    balance: null as { collateral: number; availableMargin: number; usedMargin: number; unrealizedPnl: number } | null,
    isConnected, // Raw socket connection state
    isSocketConnected: isConnected,
  };
}

/**
 * Hook to manage WebSocket client token.
 * Should be called when the user authenticates.
 */
export function useWsAuth(token: string | null) {
  useEffect(() => {
    wsClient.setToken(token);
  }, [token]);
}

/**
 * Hook to get connection state for a channel.
 */
export function useWsConnectionState(channel: string | null): ConnectionState {
  const [state, setState] = useState<ConnectionState>("disconnected");

  useEffect(() => {
    if (!channel) {
      setState("disconnected");
      return;
    }

    const checkState = () => {
      setState(wsClient.getState(channel));
    };

    // Check immediately and periodically
    checkState();
    const interval = setInterval(checkState, 1000);

    return () => clearInterval(interval);
  }, [channel]);

  return state;
}

/**
 * Engine event types from backend
 */
export type EngineEvent =
  | { type: "order_placed"; order: { id: string; user_id: string; market: string; side: string; order_type: string; price: string; size: string; status: string; created_at: string; updated_at: string } }
  | { type: "order_filled"; user_id: string; market: string; order_id: string; order_type: string; fill_price: string; fill_size: string; timestamp: string }
  | { type: "order_cancelled"; order_id: string; timestamp: string }
  | { type: "position_updated"; position: { user_id: string; market: string; size: string; entry_price: string; timestamp: string } }
  | { type: "position_liquidated"; user_id: string; market: string; liquidation_price: string; timestamp: string }
  | { type: "deposit_credited"; user_id: string; amount: string; tx_signature: string; new_balance: string; timestamp: string }
  | { type: "margin_warning"; user_id: string; market: string; margin_ratio: string; distance_to_liquidation_percent: string; warning_level: "caution" | "warning" | "danger"; timestamp: string }
  | { type: "liquidation_executed"; liquidation_id: string; user_id: string; market: string; position_size: string; entry_price: string; liquidation_price: string; mark_price: string; pnl: string; penalty_fee: string; insurance_fund_contribution: string; timestamp: string }
  | { type: "tp_sl_triggered"; user_id: string; market: string; trigger_type: string; trigger_price: string; execution_price: string; position_size: string; pnl: string; timestamp: string }
  | { type: "withdrawal_status_changed"; user_id: string; withdrawal_id: string; status: string; amount: string; token: string; rejection_reason?: string; timestamp: string }
  | { type: "account_status_changed"; user_id: string; old_status: string; new_status: string; reason?: string; timestamp: string }
  | { type: "balance_adjusted"; user_id: string; amount: string; reason: string; new_balance: string; timestamp: string };

/**
 * Hook for subscribing to engine events (global broadcast channel).
 * Used for notifications like deposits, liquidations, margin warnings, etc.
 */
export function useWsEvents(
  onEvent: (event: EngineEvent) => void,
  enabled = true
): { isConnected: boolean } {
  const [isConnected, setIsConnected] = useState(false);
  
  // Use ref to store callback to avoid re-subscribing when callback changes
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled) {
      setIsConnected(false);
      return;
    }

    const unsubscribe = wsClient.subscribe<WsMessage>("events", (message) => {
      // Engine events come as-is from the backend
      onEventRef.current(message as unknown as EngineEvent);
    });

    // Check connection state periodically
    const checkConnection = () => {
      const state = wsClient.getState("events");
      setIsConnected(state === "connected");
    };
    
    checkConnection();
    const interval = setInterval(checkConnection, 500);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [enabled]);

  return { isConnected };
}
